import React from 'react';
import { useSelector } from 'react-redux';
import { getCatalogCoverUrl } from '../pages/CatalogViewPage';
import { useTranslation } from 'react-i18next';

const VendorFooter = ({ onOpenAbout }) => {
    const { t } = useTranslation();
    const user = useSelector((state) => state.app.user.value);
    const catalog = useSelector((state) => state.app.catalog.value);

    const catalogCoverUrl = getCatalogCoverUrl(catalog);

    const isValidString = (val) => Boolean(val && typeof val === 'string' && val.trim() !== '' && val !== 'null' && val !== 'undefined');

    // Email enabled if enableEmail is not false and correo exists
    const isEmailEnabled = user?.enableEmail !== false && isValidString(user?.correo);

    // Format Phone for WhatsApp
    const countryCode = user?.cpp ? `${user.cpp}` : '';
    const phoneNum = user?.telefono ? `${user.telefono}` : '';
    const fullPhone = `${countryCode}${phoneNum}`.replace(/[^0-9]/g, '');

    // WhatsApp enabled if wp or enableWp is true (or phone present when wp not false)
    const isWhatsappEnabled = (user?.wp === true || user?.enableWp === true) && Boolean(fullPhone);

    const whatsappUrl = isWhatsappEnabled 
        ? `https://api.whatsapp.com/send?phone=${fullPhone}&text=${encodeURIComponent(`¡Hola ${user?.nombre || ''}! Vi tu catálogo y quisiera realizar una consulta.`)}` 
        : null;

    // Instagram enabled if ig_user handle exists and enableIg is not false
    const isInstagramEnabled = user?.enableIg !== false && isValidString(user?.ig_user);
    const instagramUrl = (isInstagramEnabled && user?.ig_user)
        ? (user.ig_user.startsWith('http') ? user.ig_user : `https://instagram.com/${user.ig_user.replace('@', '')}`)
        : null;

    // Facebook enabled if fb_user handle exists and enableFb is not false
    const isFacebookEnabled = user?.enableFb !== false && isValidString(user?.fb_user);
    const facebookUrl = (isFacebookEnabled && user?.fb_user)
        ? (user.fb_user.startsWith('http') ? user.fb_user : `https://facebook.com/${user.fb_user}`)
        : null;

    return (
        <footer className="footer bg-gray-900 text-white pt-40 pb-24 mt-60 position-relative overflow-hidden">
            {/* Subtle Accent Glow Top Border */}
            <div 
                className="position-absolute top-0 start-0 w-100" 
                style={{ 
                    height: '4px', 
                    background: 'linear-gradient(90deg, var(--main-600, #00517a), var(--main-400, #3399cc), var(--main-600, #00517a))' 
                }} 
            />

            <div className="container container-lg">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-24 pb-32 border-bottom border-gray-800">
                    {/* Seller Profile Info (Only seller name, logo & view info option) */}
                    <div className="d-flex align-items-center gap-16">
                        {catalogCoverUrl || user?.logoUrl ? (
                            <img 
                                src={catalogCoverUrl || user?.logoUrl} 
                                alt={user?.nombre || t('store.vendor', 'Vendedor')} 
                                className="w-56 h-56 rounded-circle object-fit-cover border border-2 border-main-500 shadow-sm" 
                            />
                        ) : (
                            <div className="w-56 h-56 rounded-circle bg-main-600 text-white flex-center text-xl fw-bold border border-2 border-main-500 shadow-sm">
                                {(user?.nombre || t('store.vendor', 'Vendedor'))[0]}
                            </div>
                        )}

                        <div>
                            <span className="text-2xs text-main-400 text-uppercase fw-bold d-block">{t('store.vendor', 'Vendedor')}</span>
                            <h5 className="fw-bold text-white mb-0 text-wrap text-break" style={{ maxWidth: '220px' }}>
                                {user?.nombre || t('store.registeredVendor', 'Vendedor Registrado')}
                            </h5>
                        </div>
                    </div>

                    {/* Socials & Chat Icon Buttons (ONLY ENABLED CHANNELS SHOWN) */}
                    <div className="d-flex align-items-center gap-12 flex-wrap">
                        {/* WhatsApp Icon */}
                        {isWhatsappEnabled && (
                            <a 
                                href={whatsappUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-48 h-48 rounded-circle bg-success text-white flex-center text-xl transition-2 hover-scale shadow-xs"
                                title={`WhatsApp (+${countryCode} ${phoneNum})`}
                            >
                                <i className="ph ph-whatsapp-logo"></i>
                            </a>
                        )}

                        {/* Email Icon */}
                        {isEmailEnabled && (
                            <a 
                                href={`mailto:${user.correo}`}
                                className="w-48 h-48 rounded-circle bg-main-600 text-white flex-center text-xl transition-2 hover-scale shadow-xs"
                                title={`Correo (${user.correo})`}
                            >
                                <i className="ph ph-envelope"></i>
                            </a>
                        )}

                        {/* Instagram Icon */}
                        {isInstagramEnabled && (
                            <a 
                                href={instagramUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-48 h-48 rounded-circle text-white flex-center text-xl transition-2 hover-scale shadow-xs"
                                style={{ backgroundColor: '#E1306C' }}
                                title={`Instagram (@${user.ig_user?.replace('@', '')})`}
                            >
                                <i className="ph ph-instagram-logo"></i>
                            </a>
                        )}

                        {/* Facebook Icon */}
                        {isFacebookEnabled && (
                            <a 
                                href={facebookUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-48 h-48 rounded-circle text-white flex-center text-xl transition-2 hover-scale shadow-xs"
                                style={{ backgroundColor: '#1877F2' }}
                                title={`Facebook (${user.fb_user})`}
                            >
                                <i className="ph ph-facebook-logo"></i>
                            </a>
                        )}

                        {/* Info Modal Icon Button */}
                        {onOpenAbout && (
                            <button 
                                type="button" 
                                onClick={onOpenAbout}
                                className="w-48 h-48 rounded-circle bg-gray-800 text-gray-300 hover-text-white flex-center text-xl transition-2 border border-gray-700 hover-bg-main-600 hover-border-main-600 shadow-xs"
                                title="Ver información del vendedor"
                            >
                                <i className="ph ph-info"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Bottom Bar / Copyright */}
                <div className="pt-20 flex-between flex-wrap gap-12 align-items-center text-xs text-gray-500">
                    <p className="mb-0">
                        © {new Date().getFullYear()} <span className="text-gray-300 fw-bold">{user?.nombre || 'Vendedor'}</span>. {t('footer.allRightsReserved')}
                    </p>
                    <span className="text-2xs text-gray-600">
                        {t('footer.poweredBy')}
                    </span>
                </div>
            </div>
        </footer>
    );
};

export default VendorFooter;
